import { Alert, Box, Card, CardContent, Chip, Typography } from "@mui/material";
import type {
  SecurityEvent,
  SecurityOutcome,
  SecuritySeverity,
  SecuritySource,
  SecurityTotals,
} from "@xop/dashboard-shared";
import { SECURITY_OUTCOMES, SECURITY_SOURCES } from "@xop/dashboard-shared";
import { useCallback, useEffect, useMemo, useState } from "react";

import { EmptyState } from "@/components/ui/empty-state";
import { usePagedList } from "../../../hooks/usePagedList.js";
import { tokenStore } from "../../../lib/tokenStore.js";
import { securityApi, type SecurityFeedParams } from "./api.js";
import { BreakdownCard } from "./security-panel/BreakdownCard.js";
import {
  fallbackTotals,
  OUTCOME_LABELS,
  REFRESH_MS,
  type Tone,
  windowLabel,
  ZERO_TOTALS,
} from "./security-panel/securityFormat.js";
import { SecurityControls } from "./security-panel/SecurityControls.js";
import { SecurityFeedTable } from "./security-panel/SecurityFeedTable.js";
import { StatTile } from "./security-panel/StatTile.js";

/**
 * System Admin ▸ Security (`/admin/security`).
 *
 * The 24/7 access monitor. It surfaces BOTH halves of authorization:
 *   - blocks — every 401/403 denial, failed login, 2FA-enrollment block; and
 *   - successes — every successful hit on a sensitive surface, with the ones
 *     that succeeded WITHOUT the gating permission flagged as **anomalous
 *     access** (`authz.bypass` — an upstream failed to enforce). See
 *     `apps/server/src/auth/accessClassifier.ts`.
 *
 * A permission an admin granted personally in /admin/users shows up as a
 * `warn` `authz.elevated_access` trail row instead, coalesced per day — it is
 * a decision this dashboard recorded, not an intrusion, and treating it as an
 * alarm buried the real signals (2026-08-27).
 *
 * This file is the data orchestrator: the windowed feed fetch + 30s
 * auto-refresh, the filter state, the client-side actor narrowing, and the
 * derived breakdowns. The filter bar lives in `security-panel/SecurityControls`,
 * the feed table in `SecurityFeedTable`, the tiles/bars in `StatTile` +
 * `BreakdownCard`, and the pure formatters in `securityFormat`.
 *
 * No own <PageHeader> — the shared "System Admin" header + tab strip live
 * in AdminLayout.tsx (same contract as the other tabs).
 */
export function SecurityPanel(): JSX.Element {
  const accessToken = tokenStore.getAccess();
  const authHeader = accessToken ? `Bearer ${accessToken}` : undefined;

  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [totals, setTotals] = useState<SecurityTotals>(ZERO_TOTALS);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [hours, setHours] = useState(24);
  const [minSeverity, setMinSeverity] = useState<"" | SecuritySeverity>("");
  const [outcome, setOutcome] = useState<"" | SecurityOutcome>("");
  const [source, setSource] = useState<"" | SecuritySource>("");
  const [actorQuery, setActorQuery] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: SecurityFeedParams = { sinceHours: hours, limit: 500 };
      if (minSeverity) params.minSeverity = minSeverity;
      if (outcome) params.outcome = outcome;
      if (source) params.source = source;
      const feed = await securityApi.feed(params, authHeader);
      setEvents(feed.events);
      setSummary(feed.summary);
      setTotals(feed.totals ?? fallbackTotals(feed.events));
      setGeneratedAt(feed.generated_at);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [authHeader, hours, minSeverity, outcome, source]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // 24/7 monitor — keep the feed live without a manual click.
  useEffect(() => {
    const id = setInterval(() => void refresh(), REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Actor search is a client-side narrowing of the loaded page (no refetch):
  // match across actor / action / ip / detail so an investigator can pivot fast.
  const filtered = useMemo(() => {
    const q = actorQuery.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) => {
      const hay = `${e.actor ?? ""} ${e.action} ${e.ip ?? ""} ${
        e.detail ? JSON.stringify(e.detail) : ""
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [events, actorQuery]);

  const topActions = useMemo(
    () =>
      Object.entries(summary)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6),
    [summary],
  );

  const sourceBreakdown = useMemo(
    () =>
      SECURITY_SOURCES.map((s) => ({ key: s, n: totals.bySource[s] ?? 0 })).filter(
        (r) => r.n > 0,
      ),
    [totals],
  );

  const blocked = totals.byOutcome.denied + totals.byOutcome.failed;

  // Paginate the (optionally actor-filtered) feed at 25/page — the window can
  // hold hundreds of events (limit=500) and dumping them all is unusable.
  const { paged, page, perPage, totalCount, totalPages, setPage, setPerPage } =
    usePagedList(filtered, 25);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <SecurityControls
        hours={hours}
        onHours={setHours}
        minSeverity={minSeverity}
        onMinSeverity={setMinSeverity}
        outcome={outcome}
        onOutcome={setOutcome}
        source={source}
        onSource={setSource}
        actorQuery={actorQuery}
        onActorQuery={setActorQuery}
        loading={loading}
        generatedAt={generatedAt}
        onRefresh={() => void refresh()}
      />

      {error && (
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      )}

      {/* Window-wide headline tiles (independent of the table filters). */}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
        }}
      >
        <StatTile label="Events" value={totals.total} hint={`last ${windowLabel(hours)}`} />
        <StatTile
          label="Anomalous access"
          value={totals.anomalies}
          tone="danger"
          hint="succeeded but shouldn't have"
        />
        <StatTile label="Blocked" value={blocked} tone="warn" hint="denied + failed" />
        <StatTile
          label="Successful access"
          value={totals.byOutcome.success}
          tone="good"
          hint="sensitive reads + writes"
        />
      </Box>

      {/* The alarm banner — only when something got through that shouldn't. */}
      {totals.anomalies > 0 && (
        <Alert severity="error" variant="outlined" role="status" icon={false}>
          <Box component="strong">{totals.anomalies}</Box> anomalous access
          {totals.anomalies === 1 ? " event" : " events"} in this window — a request
          succeeded <Box component="em">without</Box> the permission that gates it, so
          something upstream failed to enforce. Filter to{" "}
          <Box component="em">Critical only</Box> + <Box component="em">Successful access</Box> to
          investigate.
        </Alert>
      )}

      {/* Breakdowns: by source, by outcome, and the top event kinds. */}
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { md: "repeat(3, 1fr)" } }}>
        <BreakdownCard
          title="By source"
          rows={sourceBreakdown.map((r) => ({ label: r.key, n: r.n }))}
          empty="No events"
        />
        <BreakdownCard
          title="By outcome"
          rows={SECURITY_OUTCOMES.map((o) => ({
            label: OUTCOME_LABELS[o],
            n: totals.byOutcome[o],
            tone: (o === "success" ? "good" : "warn") as Tone,
          })).filter((r) => r.n > 0)}
          empty="No events"
        />
        <Card>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography
              sx={{
                mb: 1,
                fontSize: 12,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "text.disabled",
              }}
            >
              Top events
            </Typography>
            {topActions.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: "text.disabled" }}>No events</Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {topActions.map(([action, n]) => (
                  <Chip key={action} size="small" variant="outlined" label={`${action} · ${n}`} />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Feed */}
      {filtered.length === 0 && !loading ? (
        <EmptyState
          title="No security events match"
          description="401/403 denials, failed logins, 2FA-enrollment blocks AND successful access to sensitive surfaces appear here as they happen — across the dashboard and the connected services. Anomalous successes are flagged critical."
        />
      ) : (
        <SecurityFeedTable
          events={paged}
          page={page}
          perPage={perPage}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
        />
      )}
    </Box>
  );
}
